import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import {
  Play,
  Usb,
  Zap,
  Terminal as TerminalIcon,
  Settings,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Plug,
  Unplug,
  Upload,
  Download
} from 'lucide-react';
import { ESPLoader, LoaderOptions, Transport } from "esptool-js";

interface TerminalLog {
  time: string;
  msg: string;
  type: string;
}

function uint8ToBinaryString(data: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;

  for (let i = 0; i < data.length; i += chunkSize) {
    const sub = data.subarray(i, i + chunkSize);
    for (let j = 0; j < sub.length; j++) {
      binary += String.fromCharCode(sub[j]);
    }
  }

  return binary;
}

export default function App() {
  const [code, setCode] = useState<string>();
  const apiUrl = 'https://iotcloud.dhineu.in/backbone/compile'; // Hardcoded and hidden from user
  const [flashAddress, setFlashAddress] = useState<string>('0x00'); // Standard Arduino ESP32 app address

  // Compilation State
  const [isCompiling, setIsCompiling] = useState<boolean>(false);
  const [compileSuccess, setCompileSuccess] = useState<boolean>(false);
  const [binData, setBinData] = useState<Uint8Array | null>(null);

  // Serial/Hardware State
  const [port, setPort] = useState<any>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isFlashing, setIsFlashing] = useState<boolean>(false);
  const [flashProgress, setFlashProgress] = useState<number>(0);

  // Terminal State
  const [terminalLogs, setTerminalLogs] = useState<TerminalLog[]>([]);
  const terminalEndRef = useRef<HTMLDivElement | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null);
  const keepReadingRef = useRef<boolean>(true);

  // File Input Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const binFileInputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll terminal
  useEffect(() => {
    if (terminalEndRef.current) {
      terminalEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [terminalLogs]);

  useEffect(() => {
    const navSerial = (navigator as any).serial;

    const disconnectHandler = (event: any) => {
      if (event.target === port) {
        addLog("Device unplugged", "warning");
        disconnectSerial();
      }
    };

    if (navSerial) {
      navSerial.addEventListener("disconnect", disconnectHandler);
      return () => {
        navSerial.removeEventListener("disconnect", disconnectHandler);
      };
    }
  }, [port]);

  const addLog = (msg: string, type: string = 'info') => {
    const time = new Date().toLocaleTimeString([], { hour12: false });
    setTerminalLogs(prev => [...prev, { time, msg, type }]);
  };

  // --- Handle File Upload (.ino) ---
  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text === 'string') {
        setCode(text);
        addLog(`Loaded file: ${file.name}`, 'system');
      }
    };
    reader.readAsText(file);

    // Reset input so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // --- NEW: Handle Binary Upload (.bin) ---
  const handleBinUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const buffer = event.target?.result as ArrayBuffer;
      if (buffer) {
        setBinData(new Uint8Array(buffer));
        setCompileSuccess(true);
        addLog(`Loaded pre-compiled binary: ${file.name} (${buffer.byteLength} bytes)`, 'success');
      }
    };
    reader.readAsArrayBuffer(file);

    // Reset input
    if (binFileInputRef.current) {
      binFileInputRef.current.value = '';
    }
  };

  // --- Handle Binary Download ---
  const handleDownloadBin = () => {
    if (!binData) return;

    // Create safe copy (removes SharedArrayBuffer typing)
    const safeBuffer = new Uint8Array(binData);

    const blob = new Blob([safeBuffer], {
      type: "application/octet-stream"
    });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "firmware.bin";

    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    addLog("Downloaded firmware.bin to local device", "system");
  };

  const handleCompile = async () => {
    setIsCompiling(true);
    setCompileSuccess(false);
    setBinData(null);
    addLog(`Sending code to compiler API...`, 'system');

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      let buffer: Uint8Array;

      // Handle both JSON (Base64/Byte Array) and Raw Binary responses
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        if (data.bin) {
          // Assume base64 string
          const binaryString = window.atob(data.bin);
          const len = binaryString.length;
          buffer = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            buffer[i] = binaryString.charCodeAt(i);
          }
        } else if (data.data) {
          buffer = new Uint8Array(data.data);
        } else {
          throw new Error("JSON response didn't contain 'bin' or 'data' fields.");
        }
      } else {
        // Assume raw binary file
        const arrayBuffer = await response.arrayBuffer();
        buffer = new Uint8Array(arrayBuffer);
      }

      setBinData(buffer);
      setCompileSuccess(true);
      addLog(`Compilation successful! Ready to flash or download (${buffer.length} bytes).`, 'success');
    } catch (error: any) {
      addLog(`Compilation failed: ${error.message}`, 'error');
      // If mixed content error is suspected
      if (error.message.includes('Failed to fetch') && window.location.protocol === 'https:' && apiUrl.startsWith('http://')) {
        addLog(`NOTE: Your browser blocked the request because this site is HTTPS and the API is HTTP (Mixed Content).`, 'warning');
      }
    } finally {
      setIsCompiling(false);
    }
  };

  const connectSerial = async () => {
    if (!('serial' in navigator)) {
      addLog("Web Serial API not supported in this browser. Use Chrome/Edge and ensure you are on HTTPS or localhost.", 'error');
      return;
    }

    try {
      const navSerial = (navigator as any).serial;
      const selectedPort = await navSerial.requestPort();
      await selectedPort.open({ baudRate: 115200 });
      setPort(selectedPort);
      setIsConnected(true);
      addLog("Serial port connected at 115200 baud.", 'success');

      // Start reading loop
      keepReadingRef.current = true;
      readLoop(selectedPort);
    } catch (error: any) {
      addLog(`Failed to connect serial: ${error.message}`, 'error');
    }
  };

  const disconnectSerial = async () => {
    try {
      keepReadingRef.current = false;

      if (readerRef.current) {
        try {
          await readerRef.current.cancel();
        } catch { }

        try {
          readerRef.current.releaseLock();
        } catch { }

        readerRef.current = null;
      }

      await new Promise(r => setTimeout(r, 200));

      if (port && port.readable) {
        await port.close();
      }

      setPort(null);
      setIsConnected(false);
      addLog("Serial port disconnected", "info");

    } catch (err: any) {
      if (!err.message.includes("locked stream")) {
        addLog(`Disconnect error: ${err.message}`, "error");
      }
    }
  };

  const readLoop = async (activePort: any) => {
    if (!activePort?.readable) return;

    if (activePort.readable.locked) {
      console.log("Stream still locked, waiting...");
      await new Promise(r => setTimeout(r, 300));
    }

    if (activePort.readable.locked) {
      addLog("Serial stream still locked, retry later", "warning");
      return;
    }

    const decoder = new TextDecoder();
    const reader = activePort.readable.getReader();
    readerRef.current = reader;

    let partialLine = "";

    try {
      while (keepReadingRef.current) {
        const { value, done } = await reader.read();
        if (done) break;

        if (value) {
          const text = decoder.decode(value, { stream: true });
          partialLine += text;
          const lines = partialLine.split("\n");
          partialLine = lines.pop() || "";

          lines.forEach(line => {
            if (line.trim()) addLog(line.trim(), "serial");
          });
        }
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        addLog(`Serial read error: ${error.message}`, "error");
      }
    } finally {
      try {
        reader.releaseLock();
      } catch { }

      if (readerRef.current === reader) {
        readerRef.current = null;
      }
    }
  };

  const handleFlash = async () => {
    if (!binData) {
      addLog("No binary data available. Compile first or upload .bin.", "error");
      return;
    }

    if (!port) {
      addLog("Connect to ESP32 first.", "error");
      return;
    }

    setIsFlashing(true);
    setFlashProgress(0);

    let transport: Transport | null = null;

    try {
      keepReadingRef.current = false;

      if (readerRef.current) {
        try {
          await readerRef.current.cancel();
        } catch { }
      }

      while (port.readable?.locked) {
        await new Promise(r => setTimeout(r, 50));
      }

      if (port.readable || port.writable) {
        await port.close();
        addLog("Serial port closed for flashing...", "system");
      }

      transport = new Transport(port);

      const loader = new ESPLoader({
        transport,
        baudrate: 115200,
        romBaudrate: 115200,
        terminal: {
          clean: () => { },
          writeLine: (msg: string) => addLog(msg, "system"),
          write: (msg: string) => addLog(msg, "system"),
        }
      });

      addLog("Connecting to ESP32 bootloader...", "system");
      await loader.main();
      addLog("ESP32 bootloader connected!", "success");

      const firmware = uint8ToBinaryString(binData);

      await loader.writeFlash({
        fileArray: [
          {
            data: firmware,
            address: parseInt(flashAddress, 16)
          }
        ],
        flashSize: "keep",
        flashMode: "keep",
        flashFreq: "keep",
        eraseAll: false,
        compress: true,
        reportProgress: (_i, written, total) => {
          const progress = Math.round((written / total) * 100);
          setFlashProgress(progress);
        }
      });

      await loader.after("hard_reset");
      addLog("Flashing complete!", "success");

    } catch (err: any) {
      console.error("FLASH ERROR:", err);
      addLog(`Flashing failed: ${err?.message || err}`, "error");
    } finally {
      setIsFlashing(false);
      setFlashProgress(0);

      try {
        if (transport) {
          await transport.disconnect();
        }

        await new Promise(r => setTimeout(r, 500));

        if (port && !port.readable) {
          await port.open({ baudRate: 115200 });
        }

        keepReadingRef.current = true;
        readLoop(port);

        addLog("Serial monitor resumed", "info");

      } catch (e: any) {
        addLog(`Serial resume error: ${e.message}`, "error");
      }
    }
  };

  const clearLogs = () => setTerminalLogs([]);

  return (
    // Changed to flex-col on mobile (h-[100dvh] handles mobile safe areas)
    <div className="flex flex-col lg:flex-row h-[100dvh] bg-slate-950 text-slate-300 font-sans overflow-hidden">

      {/* Sidebar/Code Area (Top half on mobile, left half on desktop) */}
      <div className="flex-1 lg:flex-none lg:w-1/2 flex flex-col border-b lg:border-b-0 lg:border-r border-slate-800 bg-slate-900 min-h-0">
        <div className="flex items-center justify-between p-3 lg:p-4 border-b border-slate-800 bg-slate-950 shrink-0">
          <div className="flex items-center space-x-2">
            <Zap className="w-5 h-5 text-blue-500" />
            <h1 className="text-base lg:text-lg font-bold text-slate-100 tracking-wide">ESP32 Web Studio</h1>
          </div>
        </div>

        {/* Editor Wrapper */}
        <div className="flex-1 relative p-3 lg:p-4 flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase text-slate-500">main.ino</span>

            {/* File Upload Control */}
            <div>
              <input
                type="file"
                accept=".ino,.cpp,.c,.h"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileUpload}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center px-2 py-1 lg:px-3 lg:py-1.5 text-xs font-medium bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-md transition-colors border border-slate-700"
              >
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                Open File
              </button>
            </div>
          </div>

          <textarea
            value={code}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setCode(e.target.value)}
            className="flex-1 w-full bg-slate-950 text-slate-300 font-mono text-xs lg:text-sm p-3 lg:p-4 rounded-lg border border-slate-800 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
            spellCheck="false"
            placeholder="// Paste code here or open an .ino file"
          />
        </div>
      </div>

      {/* Control & Terminal Area (Bottom half on mobile, right half on desktop) */}
      <div className="flex-1 lg:flex-none lg:w-1/2 flex flex-col bg-slate-950 min-h-0">

        {/* Top Controls */}
        <div className="p-4 lg:p-6 space-y-4 lg:space-y-6 shrink-0">

          {/* Environment Warning */}
          {window.location.protocol === 'https:' && apiUrl.startsWith('http://') && (
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-2 lg:p-3 flex items-start space-x-3 text-amber-500 text-xs lg:text-sm">
              <AlertTriangle className="w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0 mt-0.5" />
              <p>
                <strong>Mixed Content Warning:</strong> You are on HTTPS, but your API is HTTP.
                The browser will block the compilation request. Run this UI locally (`localhost`) or use an HTTPS API endpoint.
              </p>
            </div>
          )}

          {/* Setup Panel */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-3 lg:p-4">
            <div className="flex items-center justify-between mb-3 lg:mb-4">
              <h2 className="text-[10px] lg:text-xs font-semibold uppercase text-slate-500 flex items-center">
                <Settings className="w-3 h-3 lg:w-4 lg:h-4 mr-2" /> Configuration
              </h2>
              
              {/* NEW: Upload .bin Control */}
              <div>
                <input
                  type="file"
                  accept=".bin"
                  ref={binFileInputRef}
                  className="hidden"
                  onChange={handleBinUpload}
                />
                <button
                  onClick={() => binFileInputRef.current?.click()}
                  className="flex items-center px-2 py-1 text-[10px] lg:text-xs font-medium bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-md border border-slate-700 transition-colors"
                >
                  <Upload className="w-3 h-3 mr-1.5" /> Upload .bin
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1">
                <label className="text-xs text-slate-400">Flash Address (Hex)</label>
                <input
                  type="text"
                  value={flashAddress}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setFlashAddress(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded px-2 py-1.5 lg:px-3 lg:py-2 text-xs lg:text-sm focus:outline-none focus:border-blue-500 font-mono"
                />
              </div>
            </div>
          </div>

          {/* Action Steps - Adaptive grid for mobile */}
          <div className="grid grid-cols-3 gap-2 lg:gap-4">

            {/* Step 1: Compile (with absolute download overlay) */}
            <div className="relative flex flex-col">
              <button
                onClick={handleCompile}
                disabled={isCompiling}
                className={`flex-1 flex flex-col items-center justify-center p-2 lg:p-4 rounded-xl border transition-all ${compileSuccess ? 'bg-green-500/10 border-green-500/50 text-green-400'
                  : 'bg-slate-900 border-slate-800 hover:border-slate-600 hover:bg-slate-800 text-slate-300'
                  }`}
              >
                {isCompiling ? <Loader2 className="w-5 h-5 lg:w-6 lg:h-6 mb-1 lg:mb-2 animate-spin text-blue-500" /> :
                  compileSuccess ? <CheckCircle2 className="w-5 h-5 lg:w-6 lg:h-6 mb-1 lg:mb-2" /> : <Play className="w-5 h-5 lg:w-6 lg:h-6 mb-1 lg:mb-2" />}
                <span className="text-xs lg:text-sm font-medium">{compileSuccess ? 'Firmware Ready' : '1. Compile'}</span>
                {binData && <span className="text-[10px] lg:text-xs opacity-70 mt-1 hidden lg:block">Ready to flash</span>}
              </button>

              {/* Dynamic Download Button - only visible when compilation is successful */}
              {binData && !isCompiling && (
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevents triggering re-compile
                    handleDownloadBin();
                  }}
                  className="absolute top-1 right-1 lg:top-2 lg:right-2 p-1.5 lg:p-2 bg-green-500 hover:bg-green-400 text-slate-950 rounded-lg shadow-md transition-all z-10"
                  title="Download .bin file"
                >
                  <Download className="w-3 h-3 lg:w-4 lg:h-4" />
                </button>
              )}
            </div>

            {/* Step 2: Connect */}
            <button
              onClick={isConnected ? disconnectSerial : connectSerial}
              className={`flex flex-col items-center justify-center p-2 lg:p-4 rounded-xl border transition-all ${isConnected ? 'bg-blue-500/10 border-blue-500/50 text-blue-400'
                : 'bg-slate-900 border-slate-800 hover:border-slate-600 hover:bg-slate-800 text-slate-300'
                }`}
            >
              {isConnected ? <Unplug className="w-5 h-5 lg:w-6 lg:h-6 mb-1 lg:mb-2" /> : <Plug className="w-5 h-5 lg:w-6 lg:h-6 mb-1 lg:mb-2" />}
              <span className="text-xs lg:text-sm font-medium">{isConnected ? '2. Disconnect' : '2. Connect'}</span>
              <span className="text-[10px] lg:text-xs opacity-70 mt-1 hidden lg:block">{isConnected ? 'Port Active' : 'Select ESP32'}</span>
            </button>

            {/* Step 3: Flash */}
            <button
              onClick={handleFlash}
              disabled={!binData || !isConnected || isFlashing}
              className={`relative overflow-hidden flex flex-col items-center justify-center p-2 lg:p-4 rounded-xl border transition-all ${(!binData || !isConnected) ? 'opacity-50 cursor-not-allowed bg-slate-900 border-slate-800 text-slate-600' :
                isFlashing ? 'bg-purple-500/10 border-purple-500/50 text-purple-400' :
                  'bg-slate-900 border-purple-500/50 hover:bg-purple-500/10 text-purple-400'
                }`}
            >
              {/* Progress Bar Background */}
              {isFlashing && (
                <div
                  className="absolute bottom-0 left-0 h-1 bg-purple-500 transition-all duration-300 ease-out"
                  style={{ width: `${flashProgress}%` }}
                />
              )}
              {isFlashing ? <Loader2 className="w-5 h-5 lg:w-6 lg:h-6 mb-1 lg:mb-2 animate-spin" /> : <Usb className="w-5 h-5 lg:w-6 lg:h-6 mb-1 lg:mb-2" />}
              <span className="text-xs lg:text-sm font-medium">3. Flash</span>
              {isFlashing ? (
                <span className="text-[10px] lg:text-xs opacity-70 mt-1 hidden lg:block">{flashProgress}%</span>
              ) : (
                <span className="text-[10px] lg:text-xs opacity-70 mt-1 hidden lg:block">to ESP32</span>
              )}
            </button>
          </div>
        </div>

        {/* Terminal Area */}
        <div className="flex-1 flex flex-col border-t border-slate-800 bg-black overflow-hidden relative min-h-0">
          <div className="flex items-center justify-between p-2 border-b border-slate-800 bg-slate-900/50 absolute top-0 w-full z-10">
            <div className="flex items-center text-[10px] lg:text-xs font-semibold uppercase text-slate-500">
              <TerminalIcon className="w-3 h-3 lg:w-4 lg:h-4 mr-2" /> Serial Monitor
            </div>
            <button onClick={clearLogs} className="text-slate-500 hover:text-slate-300 p-1">
              <Trash2 className="w-3 h-3 lg:w-4 lg:h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 lg:p-4 pt-10 lg:pt-12 font-mono text-xs lg:text-sm space-y-1">
            {terminalLogs.length === 0 && (
              <div className="text-slate-600 text-center mt-6 lg:mt-10">No output. Connect and flash to see serial data.</div>
            )}
            {terminalLogs.map((log, idx) => (
              <div key={idx} className="flex space-x-2 lg:space-x-3 hover:bg-slate-900/50 rounded px-1">
                <span className="text-slate-600 shrink-0 select-none">[{log.time}]</span>
                <span className={`break-all ${log.type === 'error' ? 'text-red-400' :
                  log.type === 'success' ? 'text-green-400' :
                    log.type === 'warning' ? 'text-amber-400' :
                      log.type === 'system' ? 'text-blue-400' :
                        'text-slate-300'
                  }`}>
                  {log.msg}
                </span>
              </div>
            ))}
            <div ref={terminalEndRef} />
          </div>
        </div>

      </div>
    </div>
  );
}